import { useEffect } from 'react';
import { useId } from '../hooks';
export var WINDOW_SCROLL_LOCK_DATA_KEY = 'hawkinsScrollLockData';
// This CSS variable can be used to offset fixed elements that aren't affected by body padding
// (see "Scroll lock for sticky elements" in https://blog.logrocket.com/create-advanced-scroll-lock-react-hook/).
export var HAWKINS_BROWSER_SCROLLBAR_WIDTH_VAR = '--hawkins-browser-scrollbar-width';
/**
 * A hook that enables a component to lock down scrolling on an element. Useful
 * in modals or full-screen components.
 *
 * @param disableScrollbarCompensation boolean to disable adding padding to compensate for the hidden scrollbar
 * @param lockScroll boolean to lock the scroll or not
 * @param element element to lock the scroll on (defaults to document.body)
 */
export function useScrollLock(_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.disableScrollbarCompensation, disableScrollbarCompensation = _c === void 0 ? false : _c, _d = _b.lockScroll, lockScroll = _d === void 0 ? false : _d, elementParam = _b.element;
    var id = useId('scrollLock');
    // Implement the scroll lock.
    // Also perform safety checks for the scenario where multiple elements are calling `useScrollLock`
    // and avoid accidentally getting stuck in a scroll-locked state.
    useEffect(function () {
        if (!lockScroll || typeof window === 'undefined')
            return;
        var element = elementParam;
        if (typeof document !== 'undefined' && element === undefined) {
            element = document.body;
        }
        if (!element)
            return;
        var computedStyle = window.getComputedStyle(element);
        var isElementDocumentBody = element === document.body;
        // This logic MUST be executed before we set `overflow: hidden` so we can calculate the scrollbar width.
        var scrollbarWidth = isElementDocumentBody
            ? // To learn more about this calculation, see:
                //   - https://developer.mozilla.org/en-US/docs/Web/API/Window/innerWidth
                //   - https://developer.mozilla.org/en-US/docs/Web/API/Element/clientWidth
                window.innerWidth - document.documentElement.clientWidth
            : // To learn more about this calculation, see:
                //   - https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetWidth
                //   - https://developer.mozilla.org/en-US/docs/Web/API/Element/clientWidth
                //   - https://developer.mozilla.org/en-US/docs/Web/API/CSS_Object_Model/Determining_the_dimensions_of_elements
                element.offsetWidth -
                    element.clientWidth -
                    (parseFloat(computedStyle.borderLeftWidth) || 0) -
                    (parseFloat(computedStyle.borderRightWidth) || 0);
        // Grab the existing scroll lock data map or instantiate a new one.
        var scrollLockDataMap = window[WINDOW_SCROLL_LOCK_DATA_KEY] || new WeakMap();
        // Grab the existing scroll lock data or instantiate it.
        var scrollLockData = scrollLockDataMap.get(element) || {
            lockIdMap: new Map(),
            // Save the original overflow-related values of the element (before we set `overflow: hidden`).
            overflow: element.style.overflow,
            overflowX: element.style.overflowX,
            overflowY: element.style.overflowY,
            // Save the original `padding-right` value of the element.
            paddingRight: element.style.paddingRight,
        };
        if (!disableScrollbarCompensation && scrollbarWidth > 0) {
            var compensationWidth = scrollbarWidth + (parseFloat(computedStyle.paddingRight) || 0);
            if (isElementDocumentBody) {
                document.body.style.setProperty(HAWKINS_BROWSER_SCROLLBAR_WIDTH_VAR, "".concat(compensationWidth, "px"));
            }
            element.style.paddingRight = isElementDocumentBody
                ? "var(".concat(HAWKINS_BROWSER_SCROLLBAR_WIDTH_VAR, ")")
                : "".concat(compensationWidth, "px");
        }
        scrollLockDataMap.set(element, scrollLockData);
        window[WINDOW_SCROLL_LOCK_DATA_KEY] = scrollLockDataMap;
        // Record the fact that this element is locking the scroll-lockable element.
        scrollLockData.lockIdMap.set(id, true);
        element.style.overflow = 'hidden';
        return function () {
            if (!element)
                return;
            var dataMap = window[WINDOW_SCROLL_LOCK_DATA_KEY];
            var data = dataMap === null || dataMap === void 0 ? void 0 : dataMap.get(element);
            // This shouldn't ever happen, but just in case the scroll lock data has been removed.
            if (!data) {
                // Prevent the scroll-lockable element from getting stuck in a locked state.
                element.style.overflow = 'visible';
                element.style.paddingRight = 'initial';
                return;
            }
            var lockIdMap = data.lockIdMap, overflow = data.overflow, overflowX = data.overflowX, overflowY = data.overflowY, paddingRight = data.paddingRight;
            // To prevent the scroll-lockable element from getting stuck in a locked state,
            // always erase the record that this element is locking the scroll-lockable element.
            // If `scrollLock` remains `true`, this effect will re-run and re-record the element ID.
            lockIdMap.delete(id);
            // If no elements are locking the scroll-lockable element...
            if (!lockIdMap.size) {
                // Unlock the scroll-lockable element.
                element.style.overflow = overflow;
                element.style.overflowX = overflowX;
                element.style.overflowY = overflowY;
                element.style.paddingRight = paddingRight;
                // Delete the scroll-lockable element's scroll lock data
                // to avoid potentially caching an out-of-date `overflowStyle`.
                dataMap === null || dataMap === void 0 ? void 0 : dataMap.delete(element);
            }
        };
    }, [disableScrollbarCompensation, elementParam, id, lockScroll]);
}
useScrollLock.displayName = 'useScrollLock';
